import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

function useClickOutside(rootRef, menuRef, onClose, enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    const handleClick = (event) => {
      const isInside =
        rootRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target);
      if (!isInside) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [enabled, menuRef, onClose, rootRef]);
}

function useMenuPosition(triggerRef, open) {
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, triggerRef]);

  return position;
}

function stopCellEvent(event) {
  event.stopPropagation();
}

export function TrackHubActivateSelect({
  options,
  value = [],
  disabled,
  inactive,
  onChange,
  emptyLabel,
  allLabel,
  menuZIndex = 2000,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuPosition = useMenuPosition(triggerRef, open);
  useClickOutside(rootRef, menuRef, () => setOpen(false), open);

  const displayLabel = useMemo(() => {
    if (!options.length) return emptyLabel;
    const selected = options.filter((opt) => value.includes(opt.appType));
    if (!selected.length) return emptyLabel;
    if (allLabel && selected.length === options.length) return allLabel;
    return selected.map((opt) => opt.label).join(", ");
  }, [allLabel, emptyLabel, options, value]);

  const toggleValue = (appType) => {
    const next = value.includes(appType)
      ? value.filter((item) => item !== appType)
      : [...value, appType];
    onChange(next);
  };

  const isTriggerDisabled = disabled || !options.length;

  return (
    <div
      className="trackhub-branches-page__cell-select"
      ref={rootRef}
      onClick={stopCellEvent}
      onMouseDown={stopCellEvent}
    >
      <button
        ref={triggerRef}
        type="button"
        className={[
          "trackhub-branches-page__cell-select-trigger",
          open ? "is-open" : "",
          isTriggerDisabled || inactive ? "is-muted" : "",
          isTriggerDisabled ? "is-disabled" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        disabled={isTriggerDisabled}
        onClick={() => {
          if (isTriggerDisabled) return;
          setOpen((prev) => !prev);
        }}
      >
        <span>{displayLabel}</span>
        <span className="trackhub-branches-page__cell-select-chevron" aria-hidden="true" />
      </button>
      {open && options.length > 0 && menuPosition
        ? createPortal(
            <div
              ref={menuRef}
              className="trackhub-branches-page__cell-menu trackhub-branches-page__cell-menu--portal trackhub-branches-page__cell-menu--activate"
              style={{
                position: "fixed",
                top: menuPosition.top,
                left: menuPosition.left,
                width: Math.max(menuPosition.width, 180),
                zIndex: menuZIndex,
              }}
              onClick={stopCellEvent}
              onMouseDown={stopCellEvent}
            >
              {options.map((opt) => (
                <label key={opt.appType} className="trackhub-branches-page__cell-menu-option">
                  <input
                    type="checkbox"
                    checked={value.includes(opt.appType)}
                    onChange={() => toggleValue(opt.appType)}
                    tabIndex={-1}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export function TrackHubShipmentSelect({
  value,
  carriers = [],
  assignedCarrierName,
  disabled,
  inactive,
  onChange,
  autoLabel,
  autoHelp,
  manualLabel,
  notIntegratedLabel,
  menuZIndex = 2000,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuPosition = useMenuPosition(triggerRef, open);
  useClickOutside(rootRef, menuRef, () => setOpen(false), open);

  const isAuto = !value || value === "auto";
  const isManual = !isAuto;

  const selectedCarrier = useMemo(() => {
    if (!isManual) return null;
    const needle = String(value || "").toUpperCase();
    if (!needle || needle === "MANUAL") return null;
    return (
      carriers.find(
        (c) => String(c.carrierId || c.id || "").toUpperCase() === needle
      ) || null
    );
  }, [carriers, isManual, value]);

  const displayLabel = inactive
    ? notIntegratedLabel
    : isAuto
      ? autoLabel
      : selectedCarrier?.name ||
        selectedCarrier?.carrierId ||
        assignedCarrierName ||
        (value && String(value).toUpperCase() !== "MANUAL" ? value : null) ||
        manualLabel ||
        autoLabel;

  const isTriggerDisabled = Boolean(disabled || inactive);

  return (
    <div
      className="trackhub-branches-page__cell-select"
      ref={rootRef}
      onClick={stopCellEvent}
      onMouseDown={stopCellEvent}
    >
      <button
        ref={triggerRef}
        type="button"
        className={[
          "trackhub-branches-page__cell-select-trigger",
          open ? "is-open" : "",
          inactive ? "is-muted" : "",
          isTriggerDisabled ? "is-disabled" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        disabled={isTriggerDisabled}
        onClick={() => {
          if (isTriggerDisabled) return;
          setOpen((prev) => !prev);
        }}
      >
        <span>{displayLabel}</span>
        <span className="trackhub-branches-page__cell-select-chevron" aria-hidden="true" />
      </button>
      {open && !isTriggerDisabled && menuPosition
        ? createPortal(
            <div
              ref={menuRef}
              className="trackhub-branches-page__cell-menu trackhub-branches-page__cell-menu--portal trackhub-branches-page__cell-menu--assign"
              style={{
                position: "fixed",
                top: menuPosition.top,
                left: menuPosition.left,
                width: Math.max(menuPosition.width, 240),
                zIndex: menuZIndex,
              }}
              onClick={stopCellEvent}
              onMouseDown={stopCellEvent}
            >
              <button
                type="button"
                className="trackhub-branches-page__cell-menu-option trackhub-branches-page__cell-menu-option--radio"
                onClick={() => {
                  onChange("auto");
                  setOpen(false);
                }}
              >
                <span className="trackhub-branches-page__cell-menu-option-row">
                  <input type="radio" readOnly tabIndex={-1} checked={isAuto} />
                  <span>{autoLabel}</span>
                </span>
                {autoHelp ? (
                  <span className="trackhub-branches-page__cell-menu-help">{autoHelp}</span>
                ) : null}
              </button>
              {carriers.map((carrier) => {
                const id = String(carrier.carrierId || carrier.id || "").toUpperCase();
                if (!id) return null;
                const checked =
                  isManual &&
                  String(value || "").toUpperCase() === id;
                return (
                  <button
                    key={id}
                    type="button"
                    className="trackhub-branches-page__cell-menu-option trackhub-branches-page__cell-menu-option--radio"
                    onClick={() => {
                      onChange(id);
                      setOpen(false);
                    }}
                  >
                    <span className="trackhub-branches-page__cell-menu-option-row">
                      <input type="radio" readOnly tabIndex={-1} checked={checked} />
                      <span>{carrier.name || id}</span>
                    </span>
                  </button>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
